import React, { useState, useMemo } from "react";
import { LifeBuoy, Search, FileText, ChevronRight, Download, BookOpen, Compass, Truck, ClipboardCheck, Wrench, FileBarChart, Users, Settings } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";

// Guides registry. Add new guides/PDFs here and they appear in the Help Center automatically.
// page = section index + 2 (page 1 is the cover).
const GUIDES = [
  {
    id: "getting-started",
    icon: Compass,
    title: { en: "Getting Started", es: "Primeros Pasos" },
    desc: { en: "Logging in, finding your way around, roles, language and where to get help.",
            es: "Iniciar sesión, ubicarte, roles, idioma y dónde obtener ayuda." },
    pdf: { en: "/help/getting-started-en.pdf", es: "/help/getting-started-es.pdf" },
    sections: [
      { page: 2, title: { en: "Welcome", es: "Bienvenido" }, kw: "welcome intro overview bienvenido introduccion resumen" },
      { page: 3, title: { en: "Logging In", es: "Iniciar Sesión" }, kw: "login pin sign in request access iniciar sesion solicitar acceso" },
      { page: 4, title: { en: "Finding Your Way Around", es: "Cómo Ubicarte" }, kw: "navigation sidebar menu sections navegacion menu secciones" },
      { page: 5, title: { en: "Roles at a Glance", es: "Roles de un Vistazo" }, kw: "roles permissions access roles permisos acceso" },
      { page: 6, title: { en: "Changing the Language", es: "Cambiar el Idioma" }, kw: "language english spanish idioma ingles espanol" },
      { page: 7, title: { en: "Getting Help", es: "Obtener Ayuda" }, kw: "help center support ayuda soporte" },
    ],
  },
  {
    id: "assets",
    icon: Truck,
    title: { en: "Assets & Vehicles", es: "Unidades y Vehículos" },
    desc: { en: "Track vehicles, service status, history and quick actions.",
            es: "Controla unidades, estado de servicio, historial y acciones rápidas." },
    pdf: { en: "/help/assets-en.pdf", es: "/help/assets-es.pdf" },
    sections: [
      { page: 2, title: { en: "The Fleet Dashboard", es: "El Panel de la Flota" }, kw: "dashboard vehicle cards filter search status panel tarjetas filtro busqueda estado" },
      { page: 3, title: { en: "Vehicle Details & History", es: "Detalles e Historial de la Unidad" }, kw: "details history engine transmission detalles historial motor transmision" },
      { page: 4, title: { en: "Recording a Service", es: "Registrar un Servicio" }, kw: "record service mileage photos notes registrar servicio kilometraje fotos notas" },
      { page: 5, title: { en: "Out of Service", es: "Fuera de Servicio" }, kw: "out of service return reason fuera de servicio regresar motivo" },
      { page: 6, title: { en: "Quick Actions", es: "Acciones Rápidas" }, kw: "service request fuel report parts request dvir solicitud combustible refaccion" },
      { page: 7, title: { en: "Adding Vehicles & Import/Export", es: "Agregar Unidades e Importar/Exportar" }, kw: "add edit vehicle import export csv agregar editar importar exportar" },
    ],
  },
  {
    id: "dvir",
    icon: ClipboardCheck,
    title: { en: "DVIR Inspections", es: "Inspecciones DVIR" },
    desc: { en: "Create, review and audit Driver Vehicle Inspection Reports.",
            es: "Crea, revisa y audita los Reportes de Inspección de Unidades." },
    pdf: { en: "/help/dvir-en.pdf", es: "/help/dvir-es.pdf" },
    sections: [
      { page: 2, title: { en: "What is a DVIR?", es: "¿Qué es un DVIR?" }, kw: "dvir inspection what is inspeccion que es" },
      { page: 3, title: { en: "Creating an Inspection", es: "Crear una Inspección" }, kw: "create inspection odometer photos 37 items crear inspeccion odometro fotos puntos" },
      { page: 4, title: { en: "Marking & Editing Defects", es: "Marcar y Editar Defectos" }, kw: "defect defective remarks edit clear defecto defectuoso comentarios editar quitar" },
      { page: 5, title: { en: "Viewing, PDF & History", es: "Ver, PDF e Historial" }, kw: "view report download pdf history ver reporte descargar historial" },
      { page: 6, title: { en: "DVIR Audit Report", es: "Reporte de Auditoría DVIR" }, kw: "audit compliance driver auditoria cumplimiento operador" },
    ],
  },
  {
    id: "service-requests",
    icon: Wrench,
    title: { en: "Service Requests", es: "Solicitudes de Servicio" },
    desc: { en: "Report issues or repairs on a unit and track them to completion.",
            es: "Reporta fallas o reparaciones en una unidad y dales seguimiento." },
    pdf: { en: "/help/service-requests-en.pdf", es: "/help/service-requests-es.pdf" },
    sections: [
      { page: 2, title: { en: "Overview", es: "Resumen" }, kw: "overview service request resumen solicitud servicio" },
      { page: 3, title: { en: "Creating a Request", es: "Crear una Solicitud" }, kw: "create request category description photos crear solicitud categoria descripcion fotos" },
      { page: 4, title: { en: "Status Workflow", es: "Flujo de Estados" }, kw: "status submitted viewed fulfilled archived estado enviada vista atendida archivada" },
      { page: 5, title: { en: "Managing Requests", es: "Gestionar Solicitudes" }, kw: "manage view archive images gestionar ver archivar imagenes" },
    ],
  },
  {
    id: "parts-inventory",
    icon: BookOpen,
    title: { en: "Parts & Inventory", es: "Refacciones e Inventario" },
    desc: { en: "From purchase to install, cores, tires, reports and audit.",
            es: "Desde la compra hasta la instalación, cores, llantas, reportes y auditoría." },
    pdf: { en: "/inventory-user-guide.pdf", es: "/inventory-user-guide-es.pdf" },
    sections: [
      { page: 2, title: { en: "Overview", es: "Resumen" }, kw: "overview chain of custody golden rule resumen cadena de custodia" },
      { page: 3, title: { en: "Getting Around & Access Levels", es: "Cómo Navegar y Niveles de Acceso" }, kw: "navigation roles permissions navegar roles permisos" },
      { page: 4, title: { en: "Setting Up: Vendors, Locations & Parts", es: "Configuración: Proveedores, Ubicaciones y Refacciones" }, kw: "setup vendor location part configuracion proveedor ubicacion refaccion" },
      { page: 5, title: { en: "Requests, Purchase Orders & Receiving", es: "Solicitudes, Órdenes de Compra y Recepción" }, kw: "request purchase order receive solicitud orden compra recibir" },
      { page: 6, title: { en: "Using Parts & Installing Tires", es: "Usar Refacciones e Instalar Llantas" }, kw: "issue install tire entregar instalar llanta" },
      { page: 7, title: { en: "Core Tracking & Credit Recovery", es: "Seguimiento de Cores y Recuperación de Crédito" }, kw: "core credit escalation core credito escalacion" },
      { page: 8, title: { en: "Counts, Adjustments, Salvage & Tires", es: "Conteos, Ajustes, Rescate y Llantas" }, kw: "adjustment cycle count salvage tire ajuste conteo rescate llanta" },
      { page: 9, title: { en: "Reports & Audit Trail", es: "Reportes y Registro de Auditoría" }, kw: "report csv audit reporte auditoria" },
      { page: 10, title: { en: "Tips & Tricks", es: "Consejos y Trucos" }, kw: "tips tricks consejos trucos" },
      { page: 11, title: { en: "Status & Glossary Reference", es: "Referencia de Estados y Glosario" }, kw: "status glossary estados glosario" },
    ],
  },
  {
    id: "reports",
    icon: FileBarChart,
    title: { en: "Reports", es: "Reportes" },
    desc: { en: "Fleet health, availability, service history, fuel and activity reports.",
            es: "Reportes de salud de flota, disponibilidad, historial de servicio, combustible y actividad." },
    pdf: { en: "/help/reports-en.pdf", es: "/help/reports-es.pdf" },
    sections: [
      { page: 2, title: { en: "Report Types", es: "Tipos de Reporte" }, kw: "health availability service history dvir fuel user activity salud disponibilidad historial combustible actividad" },
      { page: 3, title: { en: "Running a Report", es: "Generar un Reporte" }, kw: "period date month quarter year pdf periodo dia mes trimestre año" },
      { page: 4, title: { en: "Tips", es: "Consejos" }, kw: "tips month end consejos cierre de mes" },
    ],
  },
  {
    id: "users",
    icon: Users,
    title: { en: "Users & Access", es: "Usuarios y Acceso" },
    desc: { en: "Manage people, approve access requests and control roles (admins).",
            es: "Administra personas, aprueba solicitudes de acceso y controla roles (administradores)." },
    pdf: { en: "/help/users-en.pdf", es: "/help/users-es.pdf" },
    sections: [
      { page: 2, title: { en: "Overview", es: "Resumen" }, kw: "users admin overview usuarios administrador resumen" },
      { page: 3, title: { en: "Approving Access Requests", es: "Aprobar Solicitudes de Acceso" }, kw: "approve reject access request aprobar rechazar solicitud acceso" },
      { page: 4, title: { en: "Managing Users", es: "Gestionar Usuarios" }, kw: "add edit reset pin activate deactivate photo agregar editar restablecer activar desactivar foto" },
      { page: 5, title: { en: "Roles & Permissions", es: "Roles y Permisos" }, kw: "roles permissions admin technician driver roles permisos tecnico operador" },
    ],
  },
  {
    id: "settings",
    icon: Settings,
    title: { en: "Settings", es: "Configuración" },
    desc: { en: "Configure service intervals and the app language.",
            es: "Configura los intervalos de servicio y el idioma de la app." },
    pdf: { en: "/help/settings-en.pdf", es: "/help/settings-es.pdf" },
    sections: [
      { page: 2, title: { en: "Service Intervals", es: "Intervalos de Servicio" }, kw: "service interval miles hours days intervalo servicio millas horas dias" },
      { page: 3, title: { en: "App Language", es: "Idioma de la Aplicación" }, kw: "language english spanish idioma ingles espanol" },
    ],
  },
];

export const HelpCenter = () => {
  const { t, lang } = useT();
  const [query, setQuery] = useState("");
  const L = (obj) => (obj && (obj[lang] || obj.en)) || "";

  const q = query.trim().toLowerCase();

  const results = useMemo(() => {
    return GUIDES.map((g) => {
      const sections = g.sections.filter((s) => {
        if (!q) return true;
        const hay = `${L(s.title)} ${s.kw} ${L(g.title)}`.toLowerCase();
        return hay.includes(q);
      });
      return { ...g, _sections: sections };
    }).filter((g) => !q || g._sections.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, lang]);

  const pdfFor = (g) => (g.pdf[lang] || g.pdf.en);

  return (
    <div className="p-4 sm:p-6 lg:p-8 pt-16 sm:pt-6 lg:pt-8 max-w-4xl mx-auto" data-testid="help-center">
      <div className="mb-5">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
          <LifeBuoy className="w-6 h-6 text-blue-600 shrink-0" /> {t("Help Center")}
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">{t("Guides & documentation for the app")}</p>
      </div>

      <div className="relative max-w-lg mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("Search help topics...")}
          data-testid="help-search"
          className="w-full h-11 pl-9 pr-3 rounded-xl border border-slate-200 text-sm bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
        />
      </div>

      {results.length === 0 ? (
        <div className="text-center py-16 text-slate-400" data-testid="help-empty">
          <Search className="w-10 h-10 mx-auto mb-2 opacity-50" />
          <p>{t("No topics match your search")}</p>
        </div>
      ) : (
        <div className="space-y-5">
          {results.map((g) => {
            const Icon = g.icon;
            return (
              <div key={g.id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid={`help-guide-${g.id}`}>
                <div className="p-4 sm:p-5 border-b border-slate-100 flex items-start gap-3">
                  <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                    <Icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-base sm:text-lg font-bold text-slate-900">{L(g.title)}</h2>
                    <p className="text-sm text-slate-500 mt-0.5">{L(g.desc)}</p>
                  </div>
                  <a
                    href={pdfFor(g)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`help-download-${g.id}`}
                    className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors shrink-0"
                  >
                    <Download className="w-4 h-4" /> {t("Full Guide (PDF)")}
                  </a>
                </div>

                {/* Mobile full-guide button */}
                <a
                  href={pdfFor(g)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sm:hidden flex items-center justify-center gap-1.5 text-sm font-medium py-2.5 bg-blue-50 text-blue-700 border-b border-slate-100"
                >
                  <Download className="w-4 h-4" /> {t("Full Guide (PDF)")}
                </a>

                <div className="divide-y divide-slate-50">
                  <div className="px-4 sm:px-5 py-2 text-[11px] font-semibold text-slate-400 uppercase tracking-wide">
                    {g._sections.length} {t("topic(s)")}
                  </div>
                  {g._sections.map((s) => (
                    <a
                      key={s.page}
                      href={`${pdfFor(g)}#page=${s.page}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`help-topic-${g.id}-${s.page}`}
                      className="flex items-center gap-3 px-4 sm:px-5 py-3 hover:bg-slate-50 transition-colors group"
                    >
                      <FileText className="w-4 h-4 text-slate-400 group-hover:text-blue-500 shrink-0" />
                      <span className="flex-1 min-w-0 text-sm text-slate-700 group-hover:text-slate-900 truncate">{L(s.title)}</span>
                      <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500 shrink-0" />
                    </a>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
